import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { PatientEnroller } from './usePatientEnrollers';
import { PADashboardActions } from './usePADashboardState';
import { pesAdminService } from '@/services/pa/pesAdminService';

export const usePADashboardOperations = (
  actions: PADashboardActions,
  refetch: () => void
) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { toast } = useToast();

  const handleRefresh = async () => {
    actions.setIsRefreshing(true);
    try {
      await refetch();
      toast({
        title: "Refreshed",
        description: "Dashboard data has been refreshed.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh dashboard data",
        variant: "destructive",
      });
    } finally {
      actions.setIsRefreshing(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    actions.setShowLogoutDialog(false);
    toast({
      title: "Signed Out",
      description: "You have been successfully signed out.",
    });
  };

  const handleSearchPatientsClick = () => {
    navigate('/global-search');
  };

  const handleBackToDashboard = () => {
    actions.setCurrentView('dashboard');
  };

  const handleEditEnroller = (enroller: PatientEnroller) => {
    actions.setEditingEnroller(enroller);
    actions.setShowEditDialog(true);
  };

  const handleSaveEnroller = () => {
    refetch();
    actions.setEditingEnroller(null);
    actions.setShowEditDialog(false);
  };

  const handleDeleteEnroller = (enroller: PatientEnroller) => {
    actions.setDeletingEnroller(enroller);
    actions.setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    refetch();
    actions.setDeletingEnroller(null);
    actions.setShowDeleteDialog(false);
  };

  const handleManageClinicSlots = () => {
    navigate('/appointment-slots');
  };

  const handleAddPatientEnroller = () => {
    actions.setShowAddDialog(true);
  };

  const handleAddSuccess = () => {
    refetch();
  };

  const handleSearch = () => {
    // Search is handled by real-time filtering
    actions.setCurrentPage(1);
  };

  const handleSearchReload = () => {
    actions.setSearchTerm('');
    actions.setIsSearching(false);
    refetch();
  };

  const handlePageChange = (page: number) => {
    actions.setCurrentPage(page);
  };

  const handleRecordsPerPageChange = (newRecordsPerPage: number) => {
    actions.setRecordsPerPage(newRecordsPerPage);
    actions.setCurrentPage(1);
  };

  const getFilteredPatientEnrollers = (patientEnrollers: PatientEnroller[], searchTerm: string) => {
    return patientEnrollers.filter((enroller) => {
      if (!searchTerm.trim()) return true;

      const searchLower = searchTerm.toLowerCase();
      return (
        enroller.full_name?.toLowerCase().includes(searchLower) ||
        enroller.email?.toLowerCase().includes(searchLower)
      );
    });
  };

  const getPaginationData = (
    filteredData: PatientEnroller[],
    currentPage: number,
    recordsPerPage: number
  ) => {
    return pesAdminService.paginateData(filteredData, currentPage, recordsPerPage);
  };

  const getDashboardStats = (patientEnrollers: PatientEnroller[]) => {
    return pesAdminService.calculateDashboardStats(patientEnrollers);
  };

  const renderClinicNames = (clinicNames: string) => {
    return pesAdminService.formatClinicNames(clinicNames);
  };

  return {
    handleRefresh,
    handleLogout,
    handleSearchPatientsClick,
    handleBackToDashboard,
    handleEditEnroller,
    handleSaveEnroller,
    handleDeleteEnroller,
    handleConfirmDelete,
    handleManageClinicSlots,
    handleAddPatientEnroller,
    handleAddSuccess,
    handleSearch,
    handleSearchReload,
    handlePageChange,
    handleRecordsPerPageChange,
    getFilteredPatientEnrollers,
    getPaginationData,
    getDashboardStats,
    renderClinicNames,
  };
};